import { useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import { usePlaybookNameMap, resolvePlaybookName } from '../hooks/use-playbook-name-map';
import { usePlaybookTransitions } from '../hooks/use-playbook-transitions';

import { PlaybookTransitionReviewModal } from './PlaybookTransitionReviewModal';

/**
 * PlaybookTransitionPendingBanner — S7 PR3 (S6 Condução Governada, Q-J6).
 *
 * Indicador NÃO-intrusivo na Conversa (molde `PlaybookTransitionIndicator.tsx`,
 * que é informativo/auto-dismiss) — este é DECISÓRIO: some só quando o
 * atendente decide (Confirmar/Rejeitar no modal), nunca por timeout. Aparece
 * só quando há uma `PlaybookTransitionProposal` `awaiting_confirmation` para
 * a conversa (S6 gated OFF por padrão ⇒ nunca aparece em produção hoje).
 *
 * Self-contido (mesmo padrão de `AiConductingBanner`): gateia a cap +
 * o fetch aqui dentro — o caller só passa `conversationId`.
 */
export function PlaybookTransitionPendingBanner({
  conversationId,
}: {
  conversationId: string;
}): JSX.Element | null {
  const canReview = useCapability('conversation-lifecycle-strict');
  const { proposal } = usePlaybookTransitions(conversationId, canReview);
  const { nameMap } = usePlaybookNameMap(canReview && proposal !== null);
  const [modalOpen, setModalOpen] = useState(false);

  if (!canReview || proposal === null) return null;

  const toName = resolvePlaybookName(proposal.toPlaybookId, nameMap);

  return (
    <>
      <div
        className="flex items-center gap-3 border-b px-6 py-2"
        style={{
          borderColor: 'var(--warning-border)',
          background: 'var(--warning-bg)',
          color: 'var(--warning-fg)',
        }}
        role="status"
        aria-live="polite"
        data-testid="playbook-transition-pending-banner"
      >
        <span aria-hidden="true" className="text-base">
          ⏳
        </span>
        <div className="flex-1 text-sm">
          <span className="font-medium">A IA propõe trocar o playbook para</span>{' '}
          <span className="font-semibold">{toName}</span> — aguardando sua decisão.
        </div>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="btn-ghost-mini"
          data-testid="playbook-transition-pending-review"
        >
          Revisar
        </button>
      </div>

      <PlaybookTransitionReviewModal
        proposal={modalOpen ? proposal : null}
        playbookNames={nameMap}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}
